import { useEffect, useMemo, useRef, useState } from 'react';
import { BEAD_SIZE_LIST } from '../domain/beads';
import { contrastInk } from '../domain/color';
import {
  clearPattern,
  contentBounds,
  cropTo,
  fillBackground,
  flipHorizontal,
  flipVertical,
  resizePattern,
  rotate90,
  shiftPattern,
} from '../domain/pattern';
import { beadCounts, shoppingList, threadEstimate, totalBeads, wordChart } from '../domain/report';
import { STITCH_LIST, STITCH_SPECS, peyoteCount, physicalSize } from '../domain/stitches';
import { EMPTY } from '../domain/types';
import type { Anchor } from '../domain/pattern';
import type { BeadSizeId, StitchId } from '../domain/types';
import { downloadText, safeFilename } from '../io/format';
import { renderRope } from '../render/rope';
import { Field, NumberInput, Slider, Toggle, pickFile, useEditor } from './kit';

type Tab = 'pattern' | 'beads' | 'words' | 'preview';

export function RightPanel({ theme }: { theme: 'dark' | 'light' }) {
  const { state } = useEditor();
  const [tab, setTab] = useState<Tab>('pattern');

  return (
    <aside className="right-panel">
      <nav className="tabs">
        {(
          [
            ['pattern', 'Pattern'],
            ['beads', 'Beads'],
            ['words', 'Word chart'],
            ['preview', state.pattern.stitch === 'crochet' ? 'Rope' : 'Preview'],
          ] as [Tab, string][]
        ).map(([id, label]) => (
          <button key={id} className={tab === id ? 'active' : ''} onClick={() => setTab(id)}>
            {label}
          </button>
        ))}
      </nav>
      <div className="tab-body">
        {tab === 'pattern' && <PatternTab />}
        {tab === 'beads' && <BeadsTab />}
        {tab === 'words' && <WordsTab />}
        {tab === 'preview' && <PreviewTab theme={theme} />}
      </div>
    </aside>
  );
}

function PatternTab() {
  const { store, state } = useEditor();
  const p = state.pattern;
  const [w, setW] = useState(p.width);
  const [h, setH] = useState(p.height);
  const [anchor, setAnchor] = useState<Anchor>('center');
  const size = physicalSize(p);
  const stitchSpec = STITCH_SPECS[p.stitch];

  useEffect(() => {
    setW(p.width);
    setH(p.height);
  }, [p.width, p.height, p.id]);

  const straightGrid = p.stitch === 'square' || p.stitch === 'loom';

  const loadReference = async () => {
    const file = await pickFile('image/*');
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      store.set({
        reference: { src: String(reader.result), opacity: 0.45, visible: true, x: 0, y: 0, scale: 0.25 },
      });
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="stack">
      <Field label="Name">
        <input
          value={p.name}
          onChange={(e) =>
            store.commit((pat) => {
              pat.name = e.target.value;
              return pat;
            })
          }
        />
      </Field>

      <Field label="Stitch" hint={stitchSpec.blurb}>
        <select
          value={p.stitch}
          onChange={(e) =>
            store.commit((pat) => {
              pat.stitch = e.target.value as StitchId;
              return pat;
            }, 'Changed stitch')
          }
        >
          {STITCH_LIST.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
      </Field>

      {stitchSpec.supportsOddCount && (
        <p className="dim tiny">
          <strong>{peyoteCount(p) === 'odd' ? 'Odd-count' : 'Even-count'} peyote</strong> — set by the{' '}
          {p.width} beads across, not by choice. {peyoteCount(p) === 'odd'
            ? 'Every second row needs the odd-count turnaround at one edge.'
            : 'Both edges turn normally.'}{' '}
          Change the column count to switch.
        </p>
      )}

      {p.stitch === 'crochet' && (
        <Field label="Spiral shift" hint="Beads the pattern moves sideways each round. 1 is standard.">
          <NumberInput
            value={p.ropeShift ?? 1}
            min={0}
            max={Math.max(1, p.width - 1)}
            onChange={(v) =>
              store.commit((pat) => {
                pat.ropeShift = v;
                return pat;
              })
            }
          />
        </Field>
      )}

      <Field label="Bead size">
        <select
          value={p.beadSize}
          onChange={(e) =>
            store.commit((pat) => {
              pat.beadSize = e.target.value as BeadSizeId;
              return pat;
            }, 'Changed bead size')
          }
        >
          {BEAD_SIZE_LIST.map((b) => (
            <option key={b.id} value={b.id}>
              {b.label}
            </option>
          ))}
        </select>
      </Field>

      <div className="sub-block">
        <div className="mini-label">Grid size</div>
        <div className="row">
          <NumberInput value={w} onChange={setW} min={1} max={400} />
          <span className="dim">×</span>
          <NumberInput value={h} onChange={setH} min={1} max={400} />
        </div>
        <Field label="Grow/crop from">
          <select value={anchor} onChange={(e) => setAnchor(e.target.value as Anchor)}>
            {['top-left', 'top', 'top-right', 'left', 'center', 'right', 'bottom-left', 'bottom', 'bottom-right'].map(
              (a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ),
            )}
          </select>
        </Field>
        <button
          className="btn"
          disabled={w === p.width && h === p.height}
          onClick={() => store.commit((pat) => resizePattern(pat, w, h, anchor), `Resized to ${w}×${h}`)}
        >
          Apply size
        </button>
        <p className="dim tiny">
          Finished piece approx <strong>{size.w.toFixed(1)} × {size.h.toFixed(1)} mm</strong> (
          {(size.w / 25.4).toFixed(2)} × {(size.h / 25.4).toFixed(2)} in)
        </p>
      </div>

      <div className="sub-block">
        <div className="mini-label">Transform</div>
        <div className="btn-grid">
          <button className="btn" onClick={() => store.commit(flipHorizontal, 'Flipped')}>
            Flip ↔
          </button>
          <button className="btn" onClick={() => store.commit(flipVertical, 'Flipped')}>
            Flip ↕
          </button>
          <button
            className="btn"
            disabled={!straightGrid}
            title={straightGrid ? 'Rotate a quarter turn' : 'Only straight-grid stitches can be rotated'}
            onClick={() => store.commit((pat) => rotate90(pat, true), 'Rotated')}
          >
            Rotate ⟳
          </button>
          <button
            className="btn"
            disabled={!straightGrid}
            onClick={() => store.commit((pat) => rotate90(pat, false), 'Rotated')}
          >
            Rotate ⟲
          </button>
          <button className="btn" onClick={() => store.commit((pat) => shiftPattern(pat, 1, 0), 'Shifted')}>
            Shift →
          </button>
          <button className="btn" onClick={() => store.commit((pat) => shiftPattern(pat, -1, 0), 'Shifted')}>
            Shift ←
          </button>
          <button className="btn" onClick={() => store.commit((pat) => shiftPattern(pat, 0, -1), 'Shifted')}>
            Shift ↑
          </button>
          <button className="btn" onClick={() => store.commit((pat) => shiftPattern(pat, 0, 1), 'Shifted')}>
            Shift ↓
          </button>
        </div>
        <div className="btn-grid" style={{ marginTop: 6 }}>
          <button
            className="btn"
            disabled={state.primary === EMPTY}
            title="Fill every empty cell with the selected colour"
            onClick={() => store.commit((pat) => fillBackground(pat, state.primary), 'Filled background')}
          >
            Fill empty
          </button>
          <button
            className="btn"
            onClick={() => {
              const box = contentBounds(p);
              if (!box) {
                store.status('Nothing to crop to — the pattern is empty.');
                return;
              }
              store.commit((pat) => cropTo(pat, box), `Cropped to ${box.w}×${box.h}`);
            }}
          >
            Crop to art
          </button>
          <button className="btn danger" onClick={() => store.commit(clearPattern, 'Cleared')}>
            Clear all
          </button>
        </div>
      </div>

      <div className="sub-block">
        <div className="mini-label">Tracing image</div>
        {state.reference ? (
          <>
            <Toggle
              checked={state.reference.visible}
              label="Show underlay"
              onChange={(v) => store.set({ reference: { ...state.reference!, visible: v } })}
            />
            <Field label="Opacity">
              <Slider
                min={5}
                max={100}
                value={Math.round(state.reference.opacity * 100)}
                onChange={(v) => store.set({ reference: { ...state.reference!, opacity: v / 100 } })}
                suffix="%"
              />
            </Field>
            <Field label="Scale">
              <Slider
                min={2}
                max={200}
                value={Math.round(state.reference.scale * 100)}
                onChange={(v) => store.set({ reference: { ...state.reference!, scale: v / 100 } })}
                suffix="%"
              />
            </Field>
            <div className="row">
              <Field label="X">
                <NumberInput
                  value={state.reference.x}
                  min={-400}
                  max={400}
                  onChange={(v) => store.set({ reference: { ...state.reference!, x: v } })}
                />
              </Field>
              <Field label="Y">
                <NumberInput
                  value={state.reference.y}
                  min={-400}
                  max={400}
                  onChange={(v) => store.set({ reference: { ...state.reference!, y: v } })}
                />
              </Field>
            </div>
            <button className="btn" onClick={() => store.set({ reference: null })}>
              Remove underlay
            </button>
          </>
        ) : (
          <>
            <p className="dim tiny">Drop a picture behind the grid and bead over the top of it.</p>
            <button className="btn" onClick={loadReference}>
              Load image…
            </button>
          </>
        )}
      </div>

      <div className="sub-block">
        <div className="mini-label">Notes</div>
        <textarea
          rows={3}
          value={p.meta.notes}
          placeholder="Thread, needle, finishing notes…"
          onChange={(e) =>
            store.commit((pat) => {
              pat.meta.notes = e.target.value;
              return pat;
            })
          }
        />
      </div>
    </div>
  );
}

function BeadsTab() {
  const { state } = useEditor();
  const p = state.pattern;
  const rows = useMemo(() => beadCounts(p), [p, state.rev]);
  const total = totalBeads(p);

  return (
    <div className="stack">
      <div className="stat-row">
        <div className="stat">
          <strong>{total.toLocaleString()}</strong>
          <span>beads</span>
        </div>
        <div className="stat">
          <strong>{rows.length}</strong>
          <span>colours</span>
        </div>
        <div className="stat">
          <strong>{threadEstimate(p).toFixed(1)} m</strong>
          <span>thread (est.)</span>
        </div>
      </div>

      {rows.length === 0 && <p className="dim">Nothing beaded yet.</p>}

      <table className="counts">
        <thead>
          <tr>
            <th />
            <th>Colour</th>
            <th className="num">Beads</th>
            <th className="num">g</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.index}>
              <td>
                <span
                  className="dot"
                  style={{ background: r.color?.hex, color: contrastInk(r.color?.hex ?? '#000') }}
                >
                  {state.view.showSymbols ? r.symbol : ''}
                </span>
              </td>
              <td>
                <div className="cell-name">{r.color?.name}</div>
                <div className="dim tiny">
                  {r.color?.code} {r.percent.toFixed(1)}%
                </div>
              </td>
              <td className="num">{r.count.toLocaleString()}</td>
              <td className="num">{r.grams.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {rows.length > 0 && (
        <>
          <button
            className="btn"
            onClick={() => downloadText(`${safeFilename(p.name)}-beads.txt`, shoppingList(p), 'text/plain')}
          >
            Download bead list
          </button>
          <p className="dim tiny">
            Weights include ~12% spares. Bead counts per gram are nominal and vary by finish.
          </p>
        </>
      )}
    </div>
  );
}

function WordsTab() {
  const { state, store } = useEditor();
  const p = state.pattern;
  const [useCodes, setUseCodes] = useState(true);
  const [runLength, setRunLength] = useState(true);
  const text = useMemo(
    () => wordChart(p, { useCodes, runLength }),
    [p, state.rev, useCodes, runLength],
  );

  return (
    <div className="stack">
      <Toggle checked={useCodes} onChange={setUseCodes} label="Use bead codes" />
      <Toggle checked={runLength} onChange={setRunLength} label="Group runs (3× red)" />
      <div className="row">
        <button
          className="btn"
          onClick={() => {
            navigator.clipboard?.writeText(text).then(
              () => store.status('Word chart copied'),
              () => store.status('Clipboard blocked by the browser — use Download instead'),
            );
          }}
        >
          Copy
        </button>
        <button
          className="btn"
          onClick={() => downloadText(`${safeFilename(p.name)}-wordchart.txt`, text, 'text/plain')}
        >
          Download
        </button>
      </div>
      <pre className="wordchart">{text}</pre>
    </div>
  );
}

function PreviewTab({ theme }: { theme: 'dark' | 'light' }) {
  const { state, store } = useEditor();
  const p = state.pattern;
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || p.stitch !== 'crochet') return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    renderRope(ctx, p, w, h, theme === 'dark' ? '#15171c' : '#ffffff');
  }, [p, state.rev, theme]);

  return (
    <div className="stack">
      <Toggle
        checked={state.view.mode === 'realistic'}
        onChange={(v) => store.setView({ mode: v ? 'realistic' : 'chart' })}
        label="Realistic beads on the chart"
      />
      <Toggle
        checked={state.view.showThread}
        onChange={(v) => store.setView({ showThread: v })}
        label="Show thread path"
      />
      <Toggle
        checked={state.view.showSymbols}
        onChange={(v) => store.setView({ showSymbols: v })}
        label="Show symbols (for printing in mono)"
      />

      {p.stitch === 'crochet' ? (
        <>
          <div className="mini-label">Rope preview</div>
          <canvas ref={canvasRef} className="rope-canvas" />
          <p className="dim tiny">
            {p.width} beads around, {p.height} rounds, spiralling {p.ropeShift ?? 1} bead per round. The
            far side of the tube is hidden, so turn the pattern in your head as you read it.
          </p>
        </>
      ) : (
        <p className="dim tiny">
          The rope preview is for bead crochet. For {STITCH_SPECS[p.stitch].label.toLowerCase()}, switch on
          realistic beads above to see how the finished piece will sit.
        </p>
      )}
    </div>
  );
}
